import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, RotateCcw, ShoppingBag, XCircle, Ban } from "lucide-react";
import { motion } from "framer-motion";
import { api, errMsg } from "../../lib/api";
import { fmtTRY } from "../../lib/format";
import { toast } from "sonner";

export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const orderNumber = params.get("order") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelEmail, setCancelEmail] = useState("");
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    if (!orderNumber) { setLoading(false); return; }
    const load = () => api.get(`/orders/${orderNumber}/payment/status`)
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const t = setTimeout(load, 2500); // banka callback'i gecikebilir, bir kez daha sorgula
    return () => clearTimeout(t);
  }, [orderNumber]);

  const retryPayment = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/orders/${orderNumber}/payment/start`, { installment: 1 });
      if (data.mode === "3ds" && data.action) {
        // Kart bilgileri backend'e GÖNDERİLMEZ — 3D formu doğrudan bankaya gider (popup YOK, tam sayfa yönlendirme)
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.action;
        Object.entries(data.fields || {}).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden"; input.name = k; input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      if (data.mode === "redirect" && data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      window.location.href = data.redirect_url;  // mock3d / paytr iframe
    } catch (err) {
      toast.error(errMsg(err));
      setRetrying(false);
    }
  };

  const cancelOrder = async () => {
    if (!cancelEmail.includes("@")) { toast.error("Sipariş e-posta adresinizi girin"); return; }
    setCancelling(true);
    try {
      await api.post(`/orders/${orderNumber}/cancel-unpaid`, { email: cancelEmail });
      toast.success("Siparişiniz iptal edildi");
      setData((d) => ({ ...d, payment_state: "CANCELLED", order_status: "İptal Edildi" }));
      setShowCancel(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setCancelling(false); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-3 border-amber-600 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const success = data?.payment_state === "SUCCESS";
  const cancelledOrder = data?.order_status === "İptal Edildi";
  const retriable = data && !success && !cancelledOrder;

  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Helmet><title>Ödeme Sonucu | BIGELDİ</title></Helmet>
      {success ? (
        <>
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }}>
            <CheckCircle2 size={72} className="mx-auto text-emerald-500" />
          </motion.div>
          <h1 className="font-display font-extrabold text-3xl mt-6" data-testid="payment-success-title">Ödemeniz başarıyla tamamlandı</h1>
          <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 mt-8 text-left space-y-2 text-sm" data-testid="payment-success-card">
            <div className="flex justify-between"><span className="text-neutral-500">Sipariş Numarası</span><span className="font-mono-ed font-bold" data-testid="payment-order-number">{data.order_number}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Tutar</span><span className="font-mono-ed font-bold">{fmtTRY(data.total)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Taksit</span><span>{data.installment_count === 1 ? "Tek çekim" : `${data.installment_count} taksit`}</span></div>
            {data.masked_card && <div className="flex justify-between"><span className="text-neutral-500">Kart</span><span className="font-mono-ed">{data.masked_card}</span></div>}
            <div className="flex justify-between"><span className="text-neutral-500">Sipariş Durumu</span><span className="font-semibold text-emerald-700">{data.order_status}</span></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to={`/siparis-takip`} className="px-8 py-3.5 rounded-full border-2 border-[#121214] font-semibold hover:bg-[#121214] hover:text-white transition-colors" data-testid="payment-track-btn">Siparişi Takip Et</Link>
            <Link to="/ara" className="px-8 py-3.5 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors" data-testid="payment-continue-btn">Alışverişe Devam Et</Link>
          </div>
        </>
      ) : (
        <>
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <XCircle size={72} className="mx-auto text-rose-500" />
          </motion.div>
          <h1 className="font-display font-extrabold text-3xl mt-6" data-testid="payment-failed-title">
            {cancelledOrder ? "Siparişiniz iptal edildi" : "Ödeme işlemi tamamlanamadı"}
          </h1>
          <p className="text-neutral-500 mt-3 text-sm" data-testid="payment-failed-message">
            {cancelledOrder ? "Siparişiniz iptal edildi; kartınızdan tahsilat yapılmadı."
              : data?.response_message === "3D iptal edildi" ? "3D doğrulama işlemi iptal edildi."
              : data?.response_message ? `Banka yanıtı: ${data.response_message}`
              : "Bankanız işlemi onaylamadı. Kart limitinizi veya bilgilerinizi kontrol edip tekrar deneyebilirsiniz."}
            <br />Endişelenmeyin, kartınızdan tahsilat yapılmadı.
          </p>
          {orderNumber && <p className="font-mono-ed text-sm text-neutral-400 mt-3">Sipariş: {orderNumber}</p>}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            {retriable && (
              <button onClick={retryPayment} disabled={retrying}
                className="px-8 py-3.5 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="payment-retry-btn">
                <RotateCcw size={16} /> {retrying ? "Yönlendiriliyor..." : "Tekrar Ödeme Yap"}
              </button>
            )}
            <Link to="/ara" className="px-8 py-3.5 rounded-full border-2 border-[#E6E4DD] font-semibold hover:bg-[#F4F2EB] transition-colors flex items-center justify-center gap-2" data-testid="payment-back-cart-btn">
              <ShoppingBag size={16} /> Alışverişe Dön
            </Link>
          </div>
          {retriable && !showCancel && (
            <button onClick={() => setShowCancel(true)} className="mt-5 text-xs text-neutral-400 underline hover:text-rose-600 flex items-center gap-1 mx-auto" data-testid="payment-cancel-open">
              <Ban size={12} /> Siparişi İptal Et
            </button>
          )}
          {showCancel && retriable && (
            <div className="mt-6 bg-white border border-[#E6E4DD] rounded-2xl p-5 text-left" data-testid="payment-cancel-panel">
              <p className="text-sm font-semibold mb-1">Siparişi iptal etmek istediğinize emin misiniz?</p>
              <p className="text-xs text-neutral-500 mb-3">İptal için sipariş e-posta adresinizi doğrulayın. Rezerve edilen ürünler serbest bırakılır.</p>
              <div className="flex gap-2">
                <input type="email" value={cancelEmail} onChange={(e) => setCancelEmail(e.target.value)}
                  placeholder="E-posta adresiniz" className="flex-1 h-11 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600"
                  data-testid="payment-cancel-email" />
                <button onClick={cancelOrder} disabled={cancelling}
                  className="px-5 h-11 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-500 disabled:opacity-50"
                  data-testid="payment-cancel-confirm">
                  {cancelling ? "İptal ediliyor..." : "İptali Onayla"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
